import React from "react";
import { Box } from "@mui/material";
import { format, isValid, parseISO } from "date-fns";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useConsultation } from "../../ConsultationContext";
import { BioAgeMeasurement } from "../../data/consultationTypes";
import { isEmptyBioAgeMeasurement } from "../../data/fetchConsultationPrep";

/**
 * Section 1 · Step 4 — Bio-Age Progress timeline.
 *
 * Visual reference: Figma node 14476:50887 (3-variant set: GREEN / RED / BLUE).
 *
 * Anatomy:
 *   ● ─── ›  ›  › ─── ●
 *   X yr younger     On track / X yrs younger / X yrs older
 *   BA 25            BA 28
 *   Sep 2025         May 2026
 *
 * Right-side state colour is driven by current bio vs current chronological:
 *   bio < chrono - 1   → green   "X yrs younger"
 *   bio > chrono + 1   → red     "X yrs older"
 *   |delta| <= 1       → blue    "On track"
 *
 * Color mapping (hex → theme):
 *   #20BF7D → colors_ds.SUCCESS_ICON_MAIN
 *   #F5223E → colorTokens.D[60]
 *   #1064DB → colors_ds.PRIMARY_TEXT_MAIN
 *   #3B3F42 → colors_ds.NEUTRAL_TEXT_DEFAULT
 *   #6D7075 → colors_ds.NEUTRAL_TEXT_WEAK
 *   #C5C8CC → colorTokens.N[60]
 *   #FFFFFF → colors_ds.NEUTRAL_BG_MAIN (0.5 alpha)
 *
 * The component reads `bioAge` + `bioAgeHistory` from ConsultationContext by
 * default. `currentOverride` / `previousOverride` props let callers pass mock
 * data — used by the temporary debug grid in Step4Full to preview all three
 * states without mutating the global mock.
 */
const ON_TRACK_TOLERANCE = 1;

type Props = {
  currentOverride?: BioAgeMeasurement;
  previousOverride?: BioAgeMeasurement | null;
};

/**
 * Bio-age progress track — exact SVG vector from Figma (Group 39796).
 * 377×16 viewBox containing: gradient line A (past → chevrons), gradient
 * line B (chevrons → present), past dot (left), present dot (right), and
 * three chevron arrows in the middle.
 *
 * The gradient colour stops + chevron + present-dot fill are parameterised
 * so the same vector renders all three variants (green / red / blue).
 */
type TrackTone = {
  /** Mid gradient stop (~light variant of accent). */
  mid: string;
  /** Final gradient stop = chevron + present-dot fill. */
  accent: string;
};

const ProgressTrack: React.FC<{
  tone: TrackTone;
  lightStop: string;
  pastDot: string;
}> = ({ tone, lightStop, pastDot }) => {
  const gradLeftId = React.useId();
  const gradRightId = React.useId();
  return (
    <Box
      component="svg"
      width="377"
      height="16"
      viewBox="0 0 377 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      sx={{ display: "block" }}
    >
      <defs>
        <linearGradient
          id={gradLeftId}
          x1="4.24138"
          y1="10.5"
          x2="151.759"
          y2="10.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.115385" stopColor={lightStop} />
          <stop offset="0.682692" stopColor={tone.mid} />
          <stop offset="1" stopColor={tone.accent} />
        </linearGradient>
        <linearGradient
          id={gradRightId}
          x1="229.414"
          y1="10.5"
          x2="371.586"
          y2="10.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.115385" stopColor={lightStop} />
          <stop offset="0.682692" stopColor={tone.mid} />
          <stop offset="1" stopColor={tone.accent} />
        </linearGradient>
      </defs>
      <path
        d="M9 6L7 6L7 10L9 10L9 8L9 6ZM147 10C148.105 10 149 9.10458 149 8.00001C149 6.89544 148.105 6.00001 147 6.00001L147 8.00001L147 10ZM9 8L9 10L147 10L147 8.00001L147 6.00001L9 6L9 8Z"
        fill={`url(#${gradLeftId})`}
      />
      <path
        d="M234 6C232.895 6 232 6.89543 232 8C232 9.10457 232.895 10 234 10V8V6ZM367 10H369V6H367V8V10ZM234 8V10H367V8V6H234V8Z"
        fill={`url(#${gradRightId})`}
      />
      <circle cx="5" cy="8" r="5" fill={pastDot} />
      <circle cx="372" cy="8" r="5" fill={tone.accent} />
      <path
        d="M157.303 1.36048C157.133 1.15653 156.889 1.02836 156.625 1.00417C156.361 0.979978 156.098 1.06174 155.894 1.23148C155.69 1.40122 155.562 1.64503 155.537 1.90927C155.513 2.17351 155.595 2.43653 155.765 2.64048L160.234 8.00048L155.765 13.3605C155.681 13.4615 155.617 13.578 155.578 13.7035C155.539 13.8289 155.525 13.9609 155.537 14.0917C155.549 14.2225 155.587 14.3497 155.648 14.466C155.709 14.5823 155.793 14.6854 155.894 14.7695C156.098 14.9392 156.361 15.021 156.625 14.9968C156.889 14.9726 157.133 14.8444 157.303 14.6405L162.303 8.64048C162.452 8.4608 162.534 8.23434 162.534 8.00048C162.534 7.76663 162.452 7.54017 162.303 7.36048L157.303 1.36048Z"
        fill={tone.accent}
      />
      <path
        d="M185.332 1.36048C185.162 1.15653 184.918 1.02836 184.654 1.00417C184.39 0.979978 184.127 1.06174 183.923 1.23148C183.719 1.40122 183.591 1.64503 183.567 1.90927C183.542 2.17351 183.624 2.43653 183.794 2.64048L188.263 8.00048L183.794 13.3605C183.71 13.4615 183.647 13.578 183.608 13.7035C183.569 13.8289 183.555 13.9609 183.567 14.0917C183.579 14.2225 183.616 14.3497 183.677 14.466C183.739 14.5823 183.822 14.6854 183.923 14.7695C184.127 14.9392 184.39 15.021 184.654 14.9968C184.918 14.9726 185.162 14.8444 185.332 14.6405L190.332 8.64048C190.482 8.4608 190.564 8.23434 190.564 8.00048C190.564 7.76663 190.482 7.54017 190.332 7.36048L185.332 1.36048Z"
        fill={tone.accent}
      />
      <path
        d="M215.9 1.36048C215.731 1.15653 215.487 1.02836 215.223 1.00417C214.958 0.979978 214.695 1.06174 214.491 1.23148C214.287 1.40122 214.159 1.64503 214.135 1.90927C214.111 2.17351 214.193 2.43653 214.362 2.64048L218.831 8.00048L214.362 13.3605C214.278 13.4615 214.215 13.578 214.176 13.7035C214.137 13.8289 214.123 13.9609 214.135 14.0917C214.147 14.2225 214.185 14.3497 214.246 14.466C214.307 14.5823 214.39 14.6854 214.491 14.7695C214.695 14.9392 214.958 15.021 215.223 14.9968C215.487 14.9726 215.731 14.8444 215.9 14.6405L220.9 8.64048C221.05 8.4608 221.132 8.23434 221.132 8.00048C221.132 7.76663 221.05 7.54017 220.9 7.36048L215.9 1.36048Z"
        fill={tone.accent}
      />
    </Box>
  );
};

const safeParseDate = (iso: string): Date | null => {
  // `parseISO` returns an Invalid Date object (no throw) for empty / null /
  // malformed input, so the previous try/catch never caught anything. We
  // return `null` here and let callers decide how to render the missing
  // value — `format(invalidDate, ...)` would otherwise throw at runtime
  // (which is how this exploded for customers whose backend payload has
  // `ReportDate: null`).
  if (!iso) return null;
  try {
    const d = parseISO(iso);
    return isValid(d) ? d : null;
  } catch {
    return null;
  }
};

const formatReportDate = (iso: string): string => {
  const d = safeParseDate(iso);
  return d ? format(d, "MMM yyyy") : "—";
};

const labelForMeasurement = (m: BioAgeMeasurement): string => {
  const delta = m.chronological - m.biological;
  if (delta > 0) return `${delta} yr${delta > 1 ? "s" : ""} younger`;
  if (delta < 0) {
    const yrs = Math.abs(delta);
    return `${yrs} yr${yrs > 1 ? "s" : ""} older`;
  }
  return "On track";
};

type CurrentVisual = {
  label: string;
  color: string;
};

const BioAgeProgressTimeline: React.FC<Props> = ({
  currentOverride,
  previousOverride,
}) => {
  const theme: Theme = useTheme();
  const { data } = useConsultation();
  const previous =
    previousOverride !== undefined
      ? previousOverride
      : data.prep?.bioAgeHistory ?? null;
  const current = currentOverride ?? data.bioAge;

  // Bail out for BOTH shapes the backend uses to signal "no history":
  //   • `null` — original/expected case.
  //   • `{ biological: 0, chronological: 0, reportDate: "" }` — populated
  //     placeholder that the backend sometimes ships instead of null.
  if (!previous || isEmptyBioAgeMeasurement(previous)) return null;

  const delta = current.chronological - current.biological;
  let currentVisual: CurrentVisual;
  let tone: TrackTone;
  if (Math.abs(delta) <= ON_TRACK_TOLERANCE) {
    currentVisual = {
      label: "On track",
      color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    };
    // Blue: light primary (P[40]) → primary main (P[80] / PRIMARY_TEXT_MAIN).
    tone = {
      mid: theme.colorTokens.P[40],
      accent: theme.colors_ds.PRIMARY_TEXT_MAIN,
    };
  } else if (delta > 0) {
    currentVisual = {
      label: `${delta} yr${delta > 1 ? "s" : ""} younger`,
      color: theme.colors_ds.SUCCESS_ICON_MAIN,
    };
    // Green: light success (S[40]) → SUCCESS_ICON_MAIN (S[60]).
    tone = {
      mid: theme.colorTokens.S[40],
      accent: theme.colors_ds.SUCCESS_ICON_MAIN,
    };
  } else {
    const yrs = Math.abs(delta);
    currentVisual = {
      label: `${yrs} yr${yrs > 1 ? "s" : ""} older`,
      color: theme.colorTokens.D[60],
    };
    // Red: D[40] → D[60] (matches Figma SVG exactly: #FF7583 → #F5223E).
    tone = {
      mid: theme.colorTokens.D[40],
      accent: theme.colorTokens.D[60],
    };
  }

  const pastLabel = labelForMeasurement(previous);

  return (
    <Box sx={styles.card(theme)}>
      <Box sx={styles.inner}>
        <ProgressTrack
          tone={tone}
          lightStop={theme.colorTokens.N[40]}
          pastDot={theme.colorTokens.N[40]}
        />

        <Box sx={styles.labels}>
          <Box sx={styles.endpoint}>
            <Box
              sx={styles.endpointHeadline(theme.colors_ds.NEUTRAL_TEXT_DEFAULT)}
            >
              {pastLabel}
            </Box>
            <Box sx={styles.endpointBio(theme)}>
              PNOĒ age {previous.biological}
            </Box>
            <Box sx={styles.endpointDate(theme)}>
              {formatReportDate(previous.reportDate)}
            </Box>
          </Box>
          <Box sx={styles.endpoint}>
            <Box sx={styles.endpointHeadline(currentVisual.color)}>
              {currentVisual.label}
            </Box>
            <Box sx={styles.endpointBio(theme)}>
              PNOĒ age {current.biological}
            </Box>
            <Box sx={styles.endpointDate(theme)}>
              {formatReportDate(current.reportDate)}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BioAgeProgressTimeline;

const styles = {
  card: (theme: Theme) => ({
    backgroundColor: `${theme.colors_ds.NEUTRAL_BG_MAIN}80`,
    borderRadius: "16px",
    padding: "24px",
    boxShadow:
      "0px 2px 8px rgba(0, 0, 0, 0.04), 0px 8px 20px rgba(0, 0, 0, 0.06)",
    width: "425px",
    boxSizing: "border-box" as const,
  }),
  inner: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "10px",
    width: "377px",
  },
  labels: {
    display: "flex",
    justifyContent: "space-between" as const,
    alignItems: "flex-start" as const,
    width: "100%",
  },
  endpoint: {
    width: "139px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    textAlign: "center" as const,
  },
  endpointHeadline: (color: string) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "16px",
    color,
    lineHeight: 1.6,
  }),
  endpointBio: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    lineHeight: 1.6,
  }),
  endpointDate: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colorTokens.N[60],
    lineHeight: 1.6,
  }),
};
